import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { serviceDetailsData } from "../../data/serviceDetailsData";
import { servicesData } from "../../data/servicesData";
import { Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";

const ServiceDetail = () => {
  const { parentSlug, slug } = useParams();

  const service = servicesData.find(
    (item) => item.slug === slug && item.parentSlug === parentSlug
  );

  const data = serviceDetailsData[slug];

  if (!service || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-[#fcfaf8]">
        <Helmet>
          <title>Service Not Found - Dr Meenu’s Clinic</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center py-32">
          <h1 className="text-4xl font-serif text-[#1a1a1a]">
            Service Not Found
          </h1>
          <p className="text-[#5a5a5a] mt-4">
            Please explore our other services.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const [medical, procedure, recovery] = data.sections;

  return (
    <div className="bg-[#fcfaf8] selection:bg-[#b76e79] selection:text-white">
      <Helmet>
        <title>{data.seoTitle}</title>
        <meta name="description" content={data.seoDescription} />
        <meta name="keywords" content={data.seoKeywords} />
        <meta name="author" content="Dr Meenu’s Clinic" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="overflow-hidden">

        {/* ================= HERO ================= */}
        <section className="relative h-[90vh] flex items-center justify-center text-center">
          <img
            src={data.heroImage}
            alt={data.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>

          <div className="relative z-10 max-w-4xl px-6 text-white">
            <h1 className="text-4xl md:text-6xl font-serif mb-6">
              {data.title}
            </h1>
            <p className="text-lg md:text-xl opacity-90">
              {data.intro}
            </p>
          </div>
        </section>

        {/* ================= MEDICAL ================= */}
        <section className="py-24 px-4 md:px-8 bg-gradient-to-b from-[#fcfaf8] to-white">
          <div className="max-w-7xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-serif text-[#1a1a1a] mb-4">
              {medical.heading}
            </h2>
            <p className="text-[#5a5a5a] max-w-3xl mx-auto">
              {medical.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {medical.points.map((point, i) => {
              const [title, description] = point.split(":");
              return (
                <div
                  key={i}
                  className="bg-white p-8 rounded-3xl shadow-xl border border-[#f0e4d7]"
                >
                  <h4 className="text-xl font-bold mb-3">{title}</h4>
                  <p className="text-sm text-[#5a5a5a]">{description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= PROCEDURE TIMELINE ================= */}
        <section
          className="relative py-32 px-4 md:px-8 bg-cover bg-center overflow-hidden"
          style={{
            backgroundImage: `url(${procedure.image})`,
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>

          <div className="relative max-w-6xl mx-auto">

            {/* Vertical Line */}
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: "100%" }}
              transition={{ duration: 1.2 }}
              viewport={{ once: true }}
              className="absolute left-4 md:left-1/2 top-0 w-[2px] bg-[#d6bfae] transform md:-translate-x-1/2"
            />

            {procedure.points.map((point, i) => {
              const [title, description] = point.split("-");
              const isLeft = i % 2 === 0;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isLeft ? -120 : 120 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="relative mb-24 flex items-center"
                >
                  {isLeft ? (
                    <div className="w-full md:w-1/2 md:pr-12 flex justify-end relative">
                      <div className="hidden md:block absolute right-0 top-1/2 w-12 h-[2px] bg-[#d6bfae]" />
                      <div className="bg-white/95 p-10 rounded-3xl shadow-2xl border border-[#f0e4d7] max-w-lg">
                        <h4 className="text-2xl font-serif mb-4">{title}</h4>
                        <p className="text-[#5a5a5a]">{description}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full md:w-1/2 md:pl-12 md:ml-auto relative">
                      <div className="hidden md:block absolute left-0 top-1/2 w-12 h-[2px] bg-[#d6bfae]" />
                      <div className="bg-white/95 p-10 rounded-3xl shadow-2xl border border-[#f0e4d7] max-w-lg">
                        <h4 className="text-2xl font-serif mb-4">{title}</h4>
                        <p className="text-[#5a5a5a]">{description}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ================= RECOVERY ================= */}
        <section className="py-24 px-4 md:px-8 bg-gradient-to-r from-[#1a1a1a] to-[#2b2b2b] text-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-serif text-[#d6bfae] mb-6">
                {recovery.heading}
              </h2>
              <p className="text-gray-300 mb-8">
                {recovery.description}
              </p>

              {recovery.points.map((point, i) => {
                const [title, description] = point.split("-");
                return (
                  <div key={i} className="mb-4">
                    <h4 className="font-semibold">{title}</h4>
                    <p className="text-gray-400 text-sm">{description}</p>
                  </div>
                );
              })}
            </div>

            <img
              src={recovery.image}
              alt={recovery.heading}
              className="rounded-3xl shadow-2xl"
            />
          </div>
        </section>

        {/* ================= DOCTOR ================= */}
        <section className="py-24 px-4 md:px-8 bg-[#fcfaf8]">
          <div className="max-w-4xl mx-auto text-center bg-white rounded-[3rem] shadow-2xl p-12 border border-[#f0e4d7]">
            <img
              src={data.doctorSection.image}
              alt="Doctor"
              className="w-40 h-40 rounded-full mx-auto object-cover mb-6"
            />

            <h2 className="text-3xl font-serif mb-4">
              {data.doctorSection.heading}
            </h2>

            <p className="text-[#5a5a5a] mb-8">
              {data.doctorSection.para}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {data.doctorSection.points.map((item, i) => (
                <span
                  key={i}
                  className="px-4 py-2 border border-[#d6bfae] rounded-full text-sm"
                >
                  {item}
                </span>
              ))}
            </div>

            <Link to="/contact-us">
  <button className="px-10 py-4 bg-[#b76e79] text-white rounded-full hover:bg-black transition-all">
    Request Appointment
  </button>
</Link>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ServiceDetail;